import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import { chapterAt, formatTimestamp } from "../catalog";
import type { Tutorial } from "../types";
import { TutorialVideo } from "./TutorialVideo";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tutorials: Tutorial[];
  initialId?: string;
  /** Seconds into `initialId` to start at, for a chapter link. */
  initialTime?: number;
  /** Opened on its own for a tutorial the user hasn't seen yet. */
  isNew?: boolean;
  /** Shows "See All Tutorials", for the popup. */
  onBrowseAll?: () => void;
  /** Called when a new tutorial is closed with Watch Later, before playing. */
  onWatchLater?: () => void;
};

const RAIL_ROW =
  "flex h-8 w-full items-center gap-3 rounded-md px-2 text-left text-base transition-colors hover:bg-fill-1";

/** The tutorial player: video and chapters to jump to. */
export function TutorialsDialog({
  open,
  onOpenChange,
  tutorials,
  initialId,
  initialTime = 0,
  isNew = false,
  onBrowseAll,
  onWatchLater
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [selectedId, setSelectedId] = useState(initialId);
  const [time, setTime] = useState(initialTime);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId(initialId);
    setTime(initialTime);
    setStarted(false);
  }, [open, initialId, initialTime]);

  const tutorial = tutorials.find((t) => t.id === selectedId) ?? tutorials[0];
  if (!tutorial) return null;
  const startAt = tutorial.id === initialId ? initialTime : 0;
  const playing = chapterAt(tutorial.chapters, time);
  const jumpTo = (start: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = start;
    setTime(start);
    video.play().catch(() => undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-5xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="tutorials-dialog"
      >
        <DialogHeader className="px-4 pt-4 pr-12 pb-3">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg">{tutorial.title}</DialogTitle>
            {isNew ? <StatusBadge tone="accent">New</StatusBadge> : null}
          </div>
          <DialogDescription>{tutorial.description}</DialogDescription>
        </DialogHeader>

        <div className="grid border-y border-line-2 sm:grid-cols-[minmax(0,1fr)_240px]">
          <TutorialVideo
            key={tutorial.id}
            ref={videoRef}
            tutorial={tutorial}
            startTime={startAt}
            onPlay={() => setStarted(true)}
            onTimeUpdate={setTime}
          />
          <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-2 sm:border-l sm:border-line-2">
            {tutorials.length > 1 ? (
              <nav aria-label="Tutorials" className="flex flex-col gap-0.5">
                <span className="px-2 py-1 text-sm text-fg-3">Tutorials</span>
                {tutorials.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    aria-current={t.id === tutorial.id ? "true" : undefined}
                    onClick={() => {
                      setSelectedId(t.id);
                      setTime(0);
                    }}
                    className={cn(
                      RAIL_ROW,
                      t.id === tutorial.id
                        ? "bg-fill-3 font-medium text-fg"
                        : "text-fg-2"
                    )}
                  >
                    <span className="truncate">{t.title}</span>
                  </button>
                ))}
              </nav>
            ) : null}
            <nav aria-label="Chapters" className="flex flex-col gap-0.5">
              <span className="px-2 py-1 text-sm text-fg-3">Chapters</span>
              {tutorial.chapters.map((c, i) => (
                <button
                  key={c.start}
                  type="button"
                  aria-current={i === playing ? "true" : undefined}
                  onClick={() => jumpTo(c.start)}
                  className={cn(
                    RAIL_ROW,
                    i === playing
                      ? "bg-fill-3 font-medium text-fg"
                      : "text-fg-2"
                  )}
                >
                  <span className="w-9 shrink-0 text-sm text-fg-3 tabular-nums">
                    {formatTimestamp(c.start)}
                  </span>{" "}
                  <span className="truncate">{c.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="truncate text-sm text-fg-3">
            {Math.round(tutorial.durationSecs / 60)} min
          </span>
          <div className="flex shrink-0 items-center gap-2">
            {onBrowseAll ? (
              <Button
                variant="ghost"
                onClick={onBrowseAll}
                data-testid="tutorials-browse-all"
              >
                See All Tutorials
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => {
                if (isNew && !started) onWatchLater?.();
                onOpenChange(false);
              }}
            >
              {isNew && !started ? "Watch Later" : "Close"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
